import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, getToken, onSessionExpired, setToken } from '../lib/api';

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------

type Theme = 'light' | 'dark';

type ThemeContextValue = {
  theme: Theme;
  toggle: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

const THEME_KEY = 'ccms.theme';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem(THEME_KEY) as Theme | null;
    if (stored === 'light' || stored === 'dark') return stored;

    // No explicit choice yet: follow the operating system.
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.colorScheme = theme;
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, toggle: () => setTheme((t) => (t === 'dark' ? 'light' : 'dark')) }),
    [theme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside ThemeProvider');
  return context;
}

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------

export type ToastKind = 'success' | 'error' | 'info' | 'warning';

export type Toast = {
  id: number;
  kind: ToastKind;
  message: string;
};

type ToastContextValue = {
  toasts: Toast[];
  push: (kind: ToastKind, message: string) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  dismiss: (id: number) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

let nextToastId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string) => {
      const id = nextToastId++;
      setToasts((current) => [...current, { id, kind, message }]);

      // Errors linger: the user probably needs to read and act on them.
      const lifetime = kind === 'error' ? 8000 : 4000;
      window.setTimeout(() => dismiss(id), lifetime);
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      toasts,
      push,
      dismiss,
      success: (message: string) => push('success', message),
      error: (message: string) => push('error', message),
    }),
    [toasts, push, dismiss],
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return context;
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const Roles = {
  Director: 'Director',
  Coordinator: 'Coordinator',
  Cashier: 'Cashier',
  Teacher: 'Teacher',
  Student: 'Student',
} as const;

export type Role = (typeof Roles)[keyof typeof Roles];

export type CurrentUser = {
  userId: string;
  userName: string;
  roles: Role[];

  /**
   * Which campus this deployment points at. Shown in the top bar so nobody has to guess
   * which system they are typing into. Both /auth/login and /auth/me return it, because the
   * institute endpoint proper is Director-only and this has to work for every role.
   */
  campusName?: string | null;
  instituteName?: string | null;
};

type AuthContextValue = {
  user: CurrentUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (...roles: Role[]) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Rehydrate on reload: the token lives in localStorage, but the roles come from the
  // server so a tampered token cannot grant menu items it has no claim to.
  useEffect(() => {
    let cancelled = false;

    async function restore() {
      if (!getToken()) {
        setLoading(false);
        return;
      }

      try {
        const me = await api.get<CurrentUser>('/api/auth/me');
        if (!cancelled) setUser(me);
      } catch {
        setToken(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void restore();
    return () => {
      cancelled = true;
    };
  }, []);

  // Read inside the session handler below, which is registered once and must not go stale.
  // A ref, not state, so subscribing does not churn every time the user changes.
  const signedIn = useRef(false);
  signedIn.current = user !== null;

  /*
   * An expired or revoked session is discovered by whichever request happens to be in
   * flight, anywhere in the app. Clearing the user here is what makes RequireAuth redirect
   * to /login - otherwise the token is gone but the app still believes it is signed in, and
   * the screen just fills with errors. api.ts has already cleared the token by this point.
   */

  useEffect(() => onSessionExpired(() => {
    // Only say so if they were actually signed in. On the sign-in page there is nothing to
    // expire, and a stale token failing to restore on load is not worth complaining about.
    if (signedIn.current) toast.error('Your session has expired. Please sign in again.');
    setUser(null);
  }), [toast]);

  const login = useCallback(async (username: string, password: string) => {
    const result = await api.post<{
      token: string;
      userId: string;
      userName: string;
      roles: Role[];
      campusName?: string | null;
      instituteName?: string | null;
    }>('/api/auth/login', { username, password });

    setToken(result.token);
    setUser({
      userId: result.userId,
      userName: result.userName,
      roles: result.roles,
      campusName: result.campusName,
      instituteName: result.instituteName,
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // Recording the logout is best effort; clearing the session locally is not.
    }
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      login,
      logout,
      hasRole: (...roles: Role[]) => !!user && roles.some((r) => user.roles.includes(r)),
    }),
    [user, loading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
